import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { Checkbox, Tooltip } from 'antd';
import { MenuSearchIcon } from '@/assets/icons/icons';
import type { DelayAlertFilter } from '@/proto/logiblocks/logiblocks';
import useFetchAlertThresholds from '@/hooks/useFetchAlertThresholds';
import SearchInput from '@/components/SearchInput';
import SensorInfoIcon from '@/assets/images/SensorInfoIcon';
import SetupSensorIcon from '@/assets/images/SetupSensorIcon';
import { sentenceCase } from '@/common/helper/appHelper';
import type { AlertDetail } from '@/pages/Shipments/PerShipmentNotification/components/AlertFieldByType';
import { FieldErrorLabel } from '../shared';
import type { ValidationError } from '@/pages/Logiblocks/fieldValidation';
import { isAlertTypePicked, setAlertTypesPicked } from './delayUntilEventModel';
import styles from './DelayUntilEvent.module.css';

interface DelayAlertTypeChecklistProps {
  value: DelayAlertFilter | undefined;
  onChange: (next: DelayAlertFilter) => void;
  disabled?: boolean;
  error?: ValidationError;
  /** Shown between the search box and the list (the Action radio on the Alert action taken screen). */
  header?: ReactNode;
}

const matchesSearch = (alert: AlertDetail, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    alert.alert_name?.toLowerCase().includes(q) ||
    String(alert.description ?? '')
      .toLowerCase()
      .includes(q)
  );
};

interface ChecklistRowProps {
  /** Accessible name; the visible label can carry icons and a description. */
  name: string;
  checked: boolean;
  indeterminate?: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
}

/** One checklist row: the checkbox, then the label (and description) beside it. */
const ChecklistRow = ({
  name,
  checked,
  indeterminate = false,
  disabled,
  onChange,
  children,
}: ChecklistRowProps) => (
  <Checkbox
    // `team-select-all-checkbox` is the product's black dash for a partly ticked box.
    className={`team-select-all-checkbox ${styles.checklistRow}`}
    classNames={{ label: styles.checklistRowLabel }}
    aria-label={name}
    checked={checked}
    indeterminate={indeterminate}
    disabled={disabled}
    onChange={(e) => onChange(e.target.checked)}
  >
    {children}
  </Checkbox>
);

/** The sensor icon (dotted underline is part of it) or tag the alert type's data asks for. */
const AlertSensorMarks = ({ alert }: { alert: AlertDetail }) => {
  const sensorStatus = alert.sensor_status;
  return (
    <>
      {sensorStatus !== 'setup_required' &&
        alert.sensor_required === 'required' && (
          <Tooltip title='Sensor enabled'>
            <SensorInfoIcon className='svg-icon gray-icon hover-blue' />
          </Tooltip>
        )}
      {alert.is_critical_type === true && (
        <Tooltip title='Critical alert: An alert that is indicative of a potential product compromise'>
          <SetupSensorIcon className='svg-icon gray-icon hover-red' />
        </Tooltip>
      )}
      {sensorStatus === 'optional' && (
        <span className={styles.checklistSensorOptional}>
          (Sensor optional)
        </span>
      )}
    </>
  );
};

/**
 * Searchable checklist of alert types, grouped by the phase the alert types
 * data carries (Pre-pick, Pre-flight, ...). The alert types are the same
 * ones the Alert trigger offers, and are stored as the same strings.
 */
export const DelayAlertTypeChecklist = ({
  value,
  onChange,
  disabled = false,
  error,
  header,
}: DelayAlertTypeChecklistProps) => {
  const { alertThreshold, isLoading } = useFetchAlertThresholds(null);
  const [search, setSearch] = useState('');

  const allTypes = useMemo(
    () =>
      alertThreshold.flatMap((group) => group.alerts.map((a) => a.alert_type)),
    [alertThreshold]
  );

  const visibleGroups = useMemo(
    () =>
      alertThreshold
        .map((group) => ({
          ...group,
          alerts: group.alerts.filter((a) => matchesSearch(a, search)),
        }))
        .filter((group) => group.alerts.length > 0),
    [alertThreshold, search]
  );

  const allPicked =
    allTypes.length > 0 &&
    allTypes.every((alertType) => isAlertTypePicked(value, alertType));

  const somePicked = allTypes.some((alertType) =>
    isAlertTypePicked(value, alertType)
  );

  const toggle = (types: string[], picked: boolean) =>
    onChange(setAlertTypesPicked(value, allTypes, types, picked));

  return (
    <div className={styles.checklist}>
      <div className={`${styles.checklistSearch} ${styles.addEventSearch}`}>
        <SearchInput
          placeholder='Search'
          onChange={setSearch}
          width='100%'
          style={{
            padding: '0 8px',
            backgroundColor: 'var(--button-neutral-bg)',
          }}
          prefixIcon={
            <span className={styles.addEventSearchIcon}>
              <MenuSearchIcon width={11.66} height={11.66} />
            </span>
          }
        />
      </div>
      {header}
      {isLoading && (
        <div className={styles.emptyText}>Loading alert types...</div>
      )}
      {!isLoading && alertThreshold.length === 0 && (
        <div className={styles.emptyText}>No data</div>
      )}
      {alertThreshold.length > 0 && (
        <div className={styles.checklistForm}>
          <ChecklistRow
            name='Select all'
            checked={allPicked}
            indeterminate={somePicked && !allPicked}
            disabled={disabled}
            onChange={(checked) => toggle(allTypes, checked)}
          >
            <span className={styles.checklistName}>Select all</span>
          </ChecklistRow>
          <hr className={styles.checklistDivider} />
          {visibleGroups.map((group) => {
            const types = group.alerts.map((a) => a.alert_type);
            const pickedCount = types.filter((t) =>
              isAlertTypePicked(value, t)
            ).length;
            return (
              <Fragment key={group.phase_display}>
                <div className={styles.checklistSection}>
                  <ChecklistRow
                    name={group.phase_display}
                    checked={pickedCount === types.length}
                    indeterminate={
                      pickedCount > 0 && pickedCount < types.length
                    }
                    disabled={disabled}
                    onChange={(checked) => toggle(types, checked)}
                  >
                    <span className={styles.checklistName}>
                      {group.phase_display}
                    </span>
                  </ChecklistRow>
                  {group.alerts.map((alert) => (
                    <ChecklistRow
                      key={alert.alert_type}
                      name={sentenceCase(alert.alert_name)}
                      checked={isAlertTypePicked(value, alert.alert_type)}
                      disabled={disabled}
                      onChange={(checked) =>
                        toggle([alert.alert_type], checked)
                      }
                    >
                      <span className={styles.checklistText}>
                        <span className={styles.checklistNameLine}>
                          <span className={styles.checklistName}>
                            {sentenceCase(alert.alert_name)}
                          </span>
                          <AlertSensorMarks alert={alert} />
                        </span>
                        {Boolean(alert.description) && (
                          <span className={styles.checklistHint}>
                            {String(alert.description)}
                          </span>
                        )}
                      </span>
                    </ChecklistRow>
                  ))}
                </div>
                <hr className={styles.checklistDivider} />
              </Fragment>
            );
          })}
        </div>
      )}
      <FieldErrorLabel error={error} />
    </div>
  );
};
